import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  CircularProgress,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import axiosClient from "axiosClient";
import PageHeader from "../components/PageHeader"; 


const fetchAllLegalDocuments = async () => {

  const response = await axiosClient.get("/benefits"); 
  return response.data.data || [];
};


const LegalDocumentsPage = () => {

  const { key: urlKey } = useParams(); 
  const navigate = useNavigate();
  

  const [documents, setDocuments] = useState([]);
  const [isListLoading, setIsListLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [selectedKey, setSelectedKey] = useState(false);

  //  Fetch All Documents
  useEffect(() => {
    const loadDocuments = async () => {
      try {
        setIsListLoading(true);
        const data = await fetchAllLegalDocuments();
        setDocuments(data);
        setIsError(false);
      } catch (err) {
        console.error("Failed to fetch legal documents:", err);
        setIsError(true);
      } finally {
        setIsListLoading(false);
      }
    };
    loadDocuments();
  }, []);


  useEffect(() => {
    if (documents.length > 0) {
      if (urlKey) {
        setSelectedKey(urlKey);
      } else if (documents[0] && !selectedKey) { 
        setSelectedKey(documents[0].doc_key);

        navigate(`/benefits/${documents[0].doc_key}`, { replace: true });
      }
    }
  }, [documents, urlKey, selectedKey, navigate]);
  

  const handleTabChange = useCallback((event, newKey) => {
    setSelectedKey(newKey);

    navigate(`/benefits/${newKey}`);
  }, [navigate]);


  const currentDocument = documents.find(doc => doc.doc_key === selectedKey);

  if (isListLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <CircularProgress />
      </Box>
    );
  }

  if (isError || documents.length === 0) {
    return (
      <Box textAlign="center" py={10}>
        <Typography variant="h5" color="error">
          Failed to load legal documents.
        </Typography>
      </Box>
    );
  }


  return (
    <>
      <PageHeader title="LEGAL DOCUMENTS" />
      
      {/* Main container with padding, similar to other page sections */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        
        {/* Tabs container with border and margin */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tabs 
            value={selectedKey} 
            onChange={handleTabChange} 
            aria-label="Legal Documents Tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {documents.map((doc) => (
              <Tab 
                key={doc.doc_key}
                label={doc.title} 
                value={doc.doc_key}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Document Content Area */}
        {currentDocument ? (
          <Box 
            sx={{ 
              p: { xs: 2, md: 3 },
              backgroundColor: '#fff', 
              borderRadius: 2, 
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)' 
            }}
          >
            
            <Typography 
              variant="h3" 
              gutterBottom 
              sx={{ 
                fontWeight: 700, 
                mb: 1, 
                color: "#0a1a3b"
              }}
            >
              {currentDocument.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Last Updated: {new Date(currentDocument.updated_at).toLocaleDateString()}
            </Typography>


            <Box 
              dangerouslySetInnerHTML={{ __html: currentDocument.content }}
              sx={{
                lineHeight: 1.7, 
                color: '#333',
                
          
                '& p': { 
                  marginTop: 0,
                  marginBottom: '1.25rem',
                },

              
                '& h1, & h2, & h3, & h4': {
                  marginTop: '2rem',
                  marginBottom: '1rem',
                  fontWeight: 600,
                  color: '#0D2040' 
                },

             
                '& ul, & ol': {
                  marginLeft: '20px',
                  marginBottom: '1.25rem',
                },
                '& li': {
                  marginBottom: '0.5rem',
                }
              }}
            />

          </Box>
        ) : (
          <Box textAlign="center" py={10}>
            <Typography variant="h6" color="text.secondary">
              Please select a legal document.
            </Typography>
          </Box>
        )}

      </Container>
    </>
  );
};

export default LegalDocumentsPage;